import React from "react"
import { NoExperiencesModal, DeactivateDeleteModal, RESOURCE_TYPES } from "ui/DeactivateDeleteModals"
import { CREW_DELETE_DEACTIVATE_MODES } from "../../../../../pages/guide/resources/crewMembers/constants"
import PropTypes from "prop-types"
import { Modal, Spin } from "antd"

/**
 * StaffModals component renders the appropriate modal based on mode for staff members
 * Reuses the same modal pattern as crew members
 */
const StaffModals = ({
  deactivateModalVisible,
  activateModalVisible,
  deleteModalVisible,
  selectedStaff,
  deactivateDeleteMode,
  deactivateDeleteDetails,
  onDeactivateConfirm,
  onActivateConfirm,
  onDeleteConfirm,
  onCancel,
  deactivateLoading,
  deleteLoading,
  activateLoading,
  deactivationDetailsLoading,
  deletionDetailsLoading,
  t
}) => {
  if (!selectedStaff) return null

  const staffName = selectedStaff?.name || ""
  const staffRole = selectedStaff?.role || ""
  const mode = deactivateDeleteMode
  const details = deactivateDeleteDetails || {}
  const expMessage = details.exp_message || null
  const bookingMessage = details.booking_message || null

  const getWarningMessage = (action) => {
    if (mode === CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE) {
      return null
    }
    if (expMessage) return expMessage
    if (bookingMessage) return bookingMessage
    if (action === "deactivate") {
      return t("staff.modals.warning_deactivate_remove", { name: staffName })
    }
    return t("staff.modals.warning_delete_remove", { name: staffName })
  }

  const displayName = staffRole ? `${staffName} (${staffRole})` : staffName
  const assignedTripsCount = details.assigned_trips?.length || 0
  const activeBookingsCount = details.active_bookings?.length || 0

  const getSubtitle = (action) => {
    if (action === "deactivate") {
      switch (mode) {
        case CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{staffName}</strong> {t("staff.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("staff.modals.experiences")}:
            </>
          )
        case CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{displayName}</strong> {t("staff.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("staff.modals.upcoming_trips")}. {t("staff.modals.to_deactivate")},{" "}
              {t("staff.modals.please_reassign_bookings")}.
            </>
          )
        default:
          return (
            <>
              {t("staff.modals.deactivate_staff_description_prefix")}
              <strong>{staffName}</strong>
              {t("staff.modals.deactivate_staff_description_suffix")}
            </>
          )
      }
    } else if (action === "delete") {
      switch (mode) {
        case CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{staffName}</strong> {t("staff.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("staff.modals.experiences")}:
            </>
          )
        case CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{displayName}</strong> {t("staff.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("staff.modals.upcoming_trips")}. {t("staff.modals.to_delete")},{" "}
              {t("staff.modals.please_reassign_bookings")}.
            </>
          )
        default:
          return (
            <>
              {t("staff.modals.delete_staff_description_prefix")}
              <strong>{staffName}</strong>
              {t("staff.modals.delete_staff_description_suffix")}
            </>
          )
      }
    }
    return null
  }

  // Activate modal - simple confirmation
  if (activateModalVisible) {
    return (
      <NoExperiencesModal
        open={activateModalVisible}
        title={t("staff.menu_items.activate")}
        description={t("staff.modals.activate_staff_description_prefix")}
        boldText={staffName}
        afterBoldText={t("staff.modals.activate_staff_description_suffix")}
        onCancel={onCancel}
        onOk={onActivateConfirm}
        okText={t("global.activate")}
        cancelText={t("global.cancel")}
        loading={activateLoading}
      />
    )
  }

  // Deactivate modal
  if (deactivateModalVisible) {
    const isNoExperiencesMode = mode === CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES

    // Show loading spinner while fetching details
    if (deactivationDetailsLoading) {
      return (
        <Modal
          open={deactivateModalVisible}
          title={t("staff.modals.deactivate_staff_title")}
          footer={null}
          onCancel={onCancel}
          centered
        >
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        </Modal>
      )
    }

    if (isNoExperiencesMode) {
      return (
        <NoExperiencesModal
          open={deactivateModalVisible}
          title={t("staff.modals.deactivate_staff_title")}
          description={t("staff.modals.deactivate_staff_description_prefix")}
          boldText={staffName}
          afterBoldText={t("staff.modals.deactivate_staff_description_suffix")}
          onCancel={onCancel}
          onOk={onDeactivateConfirm}
          okText={t("global.deactivate")}
          cancelText={t("global.cancel")}
          loading={deactivateLoading}
        />
      )
    }

    if (mode) {
      return (
        <DeactivateDeleteModal
          visible={deactivateModalVisible}
          onCancel={onCancel}
          onOk={onDeactivateConfirm}
          title={t("staff.modals.deactivate_staff_title")}
          subtitle={getSubtitle("deactivate")}
          okText={t("global.deactivate")}
          cancelText={t("global.cancel")}
          loading={deactivateLoading}
          mode={mode}
          list={details.assigned_trips || []}
          upcomingBookings={details.active_bookings || []}
          assignedExperiences={details.assigned_trips || []}
          warning={getWarningMessage("deactivate")}
          expMessage={expMessage}
          bookingMessage={bookingMessage}
          resourceType={RESOURCE_TYPES.STAFF}
          translationPrefix="staff"
        />
      )
    }
  }

  // Delete modal
  if (deleteModalVisible) {
    const isNoExperiencesMode = mode === CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES

    // Show loading spinner while fetching details
    if (deletionDetailsLoading) {
      return (
        <Modal
          open={deleteModalVisible}
          title={t("staff.modals.delete_staff_title")}
          footer={null}
          onCancel={onCancel}
          centered
        >
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        </Modal>
      )
    }

    if (isNoExperiencesMode) {
      return (
        <NoExperiencesModal
          open={deleteModalVisible}
          title={t("staff.modals.delete_staff_title")}
          description={t("staff.modals.delete_staff_description_prefix")}
          boldText={staffName}
          afterBoldText={t("staff.modals.delete_staff_description_suffix")}
          onCancel={onCancel}
          onOk={onDeleteConfirm}
          okText={t("global.delete")}
          cancelText={t("global.cancel")}
          loading={deleteLoading}
        />
      )
    }

    if (mode) {
      return (
        <DeactivateDeleteModal
          visible={deleteModalVisible}
          onCancel={onCancel}
          onOk={onDeleteConfirm}
          title={t("staff.modals.delete_staff_title")}
          subtitle={getSubtitle("delete")}
          okText={t("global.delete")}
          cancelText={t("global.cancel")}
          loading={deleteLoading}
          mode={mode}
          list={details.assigned_trips || []}
          upcomingBookings={details.active_bookings || []}
          assignedExperiences={details.assigned_trips || []}
          warning={getWarningMessage("delete")}
          expMessage={expMessage}
          bookingMessage={bookingMessage}
          resourceType={RESOURCE_TYPES.STAFF}
          translationPrefix="staff"
        />
      )
    }
  }

  return null
}

StaffModals.propTypes = {
  deactivateModalVisible: PropTypes.bool.isRequired,
  activateModalVisible: PropTypes.bool.isRequired,
  deleteModalVisible: PropTypes.bool.isRequired,
  selectedStaff: PropTypes.object,
  deactivateDeleteMode: PropTypes.oneOf(Object.values(CREW_DELETE_DEACTIVATE_MODES)),
  deactivateDeleteDetails: PropTypes.shape({
    exp_message: PropTypes.string,
    booking_message: PropTypes.string,
    assigned_trips: PropTypes.array,
    active_bookings: PropTypes.array
  }),
  onDeactivateConfirm: PropTypes.func.isRequired,
  onActivateConfirm: PropTypes.func.isRequired,
  onDeleteConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  deactivateLoading: PropTypes.bool,
  deleteLoading: PropTypes.bool,
  activateLoading: PropTypes.bool,
  deactivationDetailsLoading: PropTypes.bool,
  deletionDetailsLoading: PropTypes.bool,
  t: PropTypes.func.isRequired
}

StaffModals.defaultProps = {
  selectedStaff: null,
  deactivateDeleteMode: null,
  deactivateDeleteDetails: null,
  deactivateLoading: false,
  deleteLoading: false,
  activateLoading: false,
  deactivationDetailsLoading: false,
  deletionDetailsLoading: false
}

export default StaffModals
